import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import {
  Search, Plus, X, ToggleLeft, ToggleRight, AlertCircle, AlertTriangle, CheckCircle,
  Download, RefreshCw, Link, Check, RotateCcw, Trash2, FileText, Pencil, Settings, Loader2, Upload,
} from 'lucide-react';
import { downloadInvoicePdf } from '../../lib/invoiceDownload';

// ─── Types ───────────────────────────────────────────────────────────────────

interface StudentRow {
  id: string;
  first_name: string;
  last_name: string;
  class_id: string | null;
  class_title: string | null;
  plan_id: string | null;
  plan_status: string | null;
  plan_type: string | null;
  stripe_subscription_id: string | null;
  monthly_amount: number | null;
  total_amount: number | null;
  access_until: string | null;
  manual_override: boolean;
  manual_override_reason: string | null;
  notes: string | null;
  open_count: number;
  overdue_count: number;
  next_due: string | null;
}

interface Payment {
  id: string;
  plan_id: string;
  amount: number;
  currency: string;
  due_date: string;
  paid_date: string | null;
  status: string;
  payment_method: string | null;
  invoice_url: string | null;
  notes: string | null;
}

interface RawPayment {
  plan_id: string;
  status: string;
  due_date: string;
  paid_date: string | null;
  amount: number;
}

interface Invoice {
  id: string;
  invoice_number: string;
  payment_id: string | null;
  recipient_name: string;
  recipient_email: string | null;
  recipient_street: string | null;
  recipient_postal_code: string | null;
  recipient_city: string | null;
  description: string;
  service_period: string | null;
  amount: number;
  issued_at: string;
  status: string;
  pdf_path: string | null;
  notes: string | null;
  source: string;
}

const EMPTY_INVOICE_FORM = {
  recipient_name: '', recipient_street: '', recipient_postal_code: '',
  recipient_city: '', recipient_email: '', description: '',
  service_period: '', amount: '', issued_at: '', notes: '',
};

const EMPTY_SETTINGS_FORM = {
  issuer_name: '', issuer_street: '', issuer_postal_code: '', issuer_city: '',
  issuer_email: '', tax_number: '', small_business: true, tax_rate: '19', footer_text: '',
  logo_path: '', accent_color: '#970000', intro_text: '', closing_text: '',
  bank_account_holder: '', bank_iban: '', bank_bic: '', bank_name: '',
};

interface PortalClass { id: string; title: string; }

// ─── Constants ───────────────────────────────────────────────────────────────

const PLAN_STATUS_COLOR: Record<string, string> = {
  active: 'bg-green-100 text-green-700',
  paused: 'bg-yellow-100 text-yellow-700',
  completed: 'bg-blue-100 text-blue-700',
  cancelled: 'bg-gray-100 text-gray-500',
};
const PLAN_STATUS_LABEL: Record<string, string> = {
  active: 'Aktiv', paused: 'Pausiert', completed: 'Abgeschlossen', cancelled: 'Storniert',
};
const STATUS_COLOR: Record<string, string> = {
  paid: 'bg-green-100 text-green-700',
  open: 'bg-yellow-100 text-yellow-700',
  overdue: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-100 text-gray-500',
};
const STATUS_LABEL: Record<string, string> = {
  paid: 'Bezahlt', open: 'Offen', overdue: 'Überfällig', cancelled: 'Storniert',
};
const METHOD_LABEL: Record<string, string> = {
  bank_transfer: 'Überweisung', cash: 'Bar', stripe: 'Stripe', other: 'Sonstiges',
};

const fmt = (d: string | null) => d ? new Date(d + 'T00:00:00').toLocaleDateString('de-DE') : '–';
const fmtAmount = (n: number | null) => n != null ? `${n.toFixed(2)} €` : '–';
const today = () => new Date().toISOString().slice(0, 10);
const thisMonth = () => new Date().toISOString().slice(0, 7);

// ─── Toast helper ─────────────────────────────────────────────────────────────

interface Toast { id: number; type: 'success' | 'error'; message: string; }
let toastCounter = 0;

// ─── Component ───────────────────────────────────────────────────────────────

const PaymentsManager: React.FC = () => {
  const [students, setStudents] = useState<StudentRow[]>([]);
  const [allRawPayments, setAllRawPayments] = useState<RawPayment[]>([]);
  const [classes, setClasses] = useState<PortalClass[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [overdueFilter, setOverdueFilter] = useState(false);
  const [classFilter, setClassFilter] = useState('');

  // Toasts for operation feedback
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback((type: 'success' | 'error', message: string) => {
    const id = ++toastCounter;
    setToasts(prev => [...prev, { id, type, message }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4000);
  }, []);

  // Modal
  const [selected, setSelected] = useState<StudentRow | null>(null);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Plan settings edit
  const [editOverride, setEditOverride] = useState(false);
  const [editOverrideReason, setEditOverrideReason] = useState('');
  const [editAccessUntil, setEditAccessUntil] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editPlanStatus, setEditPlanStatus] = useState('');

  // New plan form
  const [showNewPlan, setShowNewPlan] = useState(false);
  const [newPlan, setNewPlan] = useState({
    class_id: '', plan_type: 'monthly', total_amount: '', monthly_amount: '',
    start_date: today(), end_date: '', access_until: '', notes: '',
    installments: '10', first_due_date: today(), day_of_month: '1',
  });

  // New single payment form
  const [showNewPayment, setShowNewPayment] = useState(false);
  const [newPayment, setNewPayment] = useState({
    amount: '', due_date: '', status: 'open', payment_method: '', invoice_url: '', notes: '',
  });

  // Quick action flash
  const [flashId, setFlashId] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<{ label: string; confirmLabel?: string; onConfirm: () => void } | null>(null);

  // Invoice inline editing (Legacy: externer Rechnungslink)
  const [editingInvoice, setEditingInvoice] = useState<string | null>(null);
  const [invoiceInput, setInvoiceInput] = useState('');

  // Generierte Rechnungen (PDF)
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [invoiceBusy, setInvoiceBusy] = useState<string | null>(null);
  const [editInvoiceRow, setEditInvoiceRow] = useState<Invoice | null>(null);
  const [invoiceForm, setInvoiceForm] = useState(EMPTY_INVOICE_FORM);

  // Rechnungs-Einstellungen (Ausstellerdaten + Design)
  const [showInvoiceSettings, setShowInvoiceSettings] = useState(false);
  const [settingsForm, setSettingsForm] = useState(EMPTY_SETTINGS_FORM);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);

  // Upload extern erstellter Rechnungs-PDFs (z. B. sevdesk)
  const [uploadForPayment, setUploadForPayment] = useState<Payment | null>(null);
  const [uploadNumber, setUploadNumber] = useState('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadBusy, setUploadBusy] = useState(false);

  // Overdue update
  const [updatingOverdue, setUpdatingOverdue] = useState(false);
  const [overdueMsg, setOverdueMsg] = useState<string | null>(null);

  // CSV month picker
  const [csvMonth, setCsvMonth] = useState(thisMonth());

  // ─── Load ─────────────────────────────────────────────────────────────────

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [
        { data: profiles, error: profilesError },
        { data: plans, error: plansError },
        { data: paysRaw, error: paysError },
        { data: classRows, error: classesError },
      ] = await Promise.all([
        supabase.from('student_profiles').select('id, first_name, last_name').eq('role', 'student').order('last_name'),
        supabase.from('student_payment_plans').select('id, student_id, class_id, plan_type, plan_status:status, stripe_subscription_id, monthly_amount, total_amount, access_until, manual_override, manual_override_reason, notes'),
        supabase.from('student_payments').select('plan_id, status, due_date, paid_date, amount'),
        supabase.from('portal_classes').select('id, title').order('title'),
      ]);

      if (profilesError) throw profilesError;
      if (plansError) throw plansError;
      if (paysError) throw paysError;
      if (classesError) throw classesError;

      setClasses((classRows ?? []) as PortalClass[]);
      setAllRawPayments((paysRaw ?? []) as RawPayment[]);

      const classMap: Record<string, string> = {};
      (classRows ?? []).forEach((c: PortalClass) => { classMap[c.id] = c.title; });

      const rows: StudentRow[] = (profiles ?? []).map((p: { id: string; first_name: string; last_name: string }) => {
        const plan = (plans ?? []).find((pl: { student_id: string }) => pl.student_id === p.id);
        const planPays = plan ? (paysRaw ?? []).filter((pay: { plan_id: string }) => pay.plan_id === plan.id) : [];
        const openCount = planPays.filter((pay: { status: string }) => pay.status === 'open').length;
        const overdueCount = planPays.filter((pay: { status: string }) => pay.status === 'overdue').length;
        const nextDue = planPays
          .filter((pay: { status: string }) => pay.status === 'open')
          .sort((a: { due_date: string }, b: { due_date: string }) => a.due_date.localeCompare(b.due_date))[0]?.due_date ?? null;

        return {
          id: p.id,
          first_name: p.first_name,
          last_name: p.last_name,
          class_id: plan?.class_id ?? null,
          class_title: plan?.class_id ? (classMap[plan.class_id] ?? null) : null,
          plan_id: plan?.id ?? null,
          plan_status: plan?.plan_status ?? null,
          plan_type: plan?.plan_type ?? null,
          stripe_subscription_id: plan?.stripe_subscription_id ?? null,
          monthly_amount: plan?.monthly_amount ?? null,
          total_amount: plan?.total_amount ?? null,
          access_until: plan?.access_until ?? null,
          manual_override: plan?.manual_override ?? false,
          manual_override_reason: plan?.manual_override_reason ?? null,
          notes: plan?.notes ?? null,
          open_count: openCount,
          overdue_count: overdueCount,
          next_due: nextDue,
        };
      });

      setStudents(rows);
    } catch (err) {
      if (import.meta.env.DEV) console.error('PaymentsManager load error:', err);
      addToast('error', 'Daten konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  const loadPayments = useCallback(async (planId: string) => {
    setPaymentsLoading(true);
    try {
      const { data, error } = await supabase.from('student_payments').select('*').eq('plan_id', planId).order('due_date');
      if (error) throw error;
      setPayments((data ?? []) as Payment[]);
    } catch (err) {
      if (import.meta.env.DEV) console.error('loadPayments error:', err);
      addToast('error', 'Zahlungen konnten nicht geladen werden.');
    } finally {
      setPaymentsLoading(false);
    }
  }, [addToast]);

  const loadInvoices = useCallback(async (studentId: string) => {
    try {
      const { data, error } = await supabase
        .from('invoices')
        .select('id, invoice_number, payment_id, recipient_name, recipient_email, recipient_street, recipient_postal_code, recipient_city, description, service_period, amount, issued_at, status, pdf_path, notes, source')
        .eq('student_id', studentId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      setInvoices((data ?? []) as Invoice[]);
    } catch (err) {
      if (import.meta.env.DEV) console.error('loadInvoices error:', err);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ─── Stats ────────────────────────────────────────────────────────────────

  const stats = useMemo(() => {
    const month = thisMonth();
    const activePlans = students.filter(s => s.plan_status === 'active').length;
    const openPayments = students.reduce((acc, s) => acc + s.open_count, 0);
    const overdueCount = students.filter(s => s.overdue_count > 0).length;
    const monthlyRevenue = allRawPayments
      .filter(p => p.status === 'paid' && p.paid_date?.startsWith(month))
      .reduce((acc, p) => acc + (p.amount ?? 0), 0);
    return { activePlans, openPayments, overdueCount, monthlyRevenue };
  }, [students, allRawPayments]);

  // Memoized class + student maps for CSV export
  const classMap = useMemo(() => {
    const m: Record<string, string> = {};
    classes.forEach(c => { m[c.id] = c.title; });
    return m;
  }, [classes]);

  const studentMap = useMemo(() => {
    const m: Record<string, string> = {};
    students.forEach(s => { m[s.id] = `${s.first_name} ${s.last_name}`; });
    return m;
  }, [students]);

  // ─── Filtered table ───────────────────────────────────────────────────────

  const filtered = useMemo(() => {
    let list = overdueFilter ? students.filter(s => s.overdue_count > 0) : students;
    if (classFilter) {
      list = classFilter === 'none'
        ? list.filter(s => !s.class_id)
        : list.filter(s => s.class_id === classFilter);
    }
    const q = search.toLowerCase().trim();
    if (!q) return list;
    return list.filter(s =>
      `${s.first_name} ${s.last_name}`.toLowerCase().includes(q) ||
      (s.class_title ?? '').toLowerCase().includes(q)
    );
  }, [students, search, overdueFilter, classFilter]);

  // ─── Modal open ───────────────────────────────────────────────────────────

  const openModal = (s: StudentRow) => {
    setSelected(s);
    setEditOverride(s.manual_override);
    setEditOverrideReason(s.manual_override_reason ?? '');
    setEditAccessUntil(s.access_until ?? '');
    setEditNotes(s.notes ?? '');
    setEditPlanStatus(s.plan_status ?? 'active');
    setShowNewPayment(false);
    setShowNewPlan(false);
    setEditingInvoice(null);
    setEditInvoiceRow(null);
    if (s.plan_id) loadPayments(s.plan_id);
    else setPayments([]);
    loadInvoices(s.id);
  };

  // ─── Save plan settings ───────────────────────────────────────────────────

  const saveSettings = async () => {
    if (!selected?.plan_id) return;
    setSaving(true);
    try {
      const { error } = await supabase.from('student_payment_plans').update({
        manual_override: editOverride,
        manual_override_reason: editOverrideReason || null,
        access_until: editAccessUntil || null,
        notes: editNotes || null,
        status: editPlanStatus,
      }).eq('id', selected.plan_id);
      if (error) throw error;

      // Abgeschlossen/Storniert beendet auch ein laufendes Stripe-Abo —
      // sonst würde Stripe unbegrenzt weiter abbuchen. "Abgeschlossen"
      // kündigt zum Periodenende, "Storniert" sofort.
      if ((editPlanStatus === 'completed' || editPlanStatus === 'cancelled') && selected.stripe_subscription_id) {
        const { data, error: fnError } = await supabase.functions.invoke('cancel-plan-subscription', {
          body: { plan_id: selected.plan_id, immediate: editPlanStatus === 'cancelled' },
        });
        if (fnError || data?.error) {
          addToast('error', 'Plan gespeichert, aber das Stripe-Abo konnte nicht gekündigt werden. Bitte im Stripe-Dashboard prüfen oder erneut speichern.');
        } else if (data?.cancelled) {
          addToast('success', editPlanStatus === 'cancelled'
            ? 'Stripe-Abo sofort gekündigt'
            : 'Stripe-Abo wird zum Periodenende beendet');
        }
      }

      addToast('success', 'Einstellungen gespeichert');
      load();
      setSelected(prev => prev ? { ...prev, manual_override: editOverride, access_until: editAccessUntil || null, plan_status: editPlanStatus } : null);
    } catch (err) {
      if (import.meta.env.DEV) console.error('saveSettings error:', err);
      addToast('error', 'Einstellungen konnten nicht gespeichert werden.');
    } finally {
      setSaving(false);
    }
  };

  // ─── Create plan + generate installments ──────────────────────────────────

  const createPlan = async () => {
    if (!selected) return;
    if (!newPlan.total_amount || !newPlan.first_due_date) {
      addToast('error', 'Bitte Gesamtbetrag und erste Fälligkeit angeben.');
      return;
    }
    setSaving(true);
    try {
      const { data: plan, error: planError } = await supabase.from('student_payment_plans').insert({
        student_id: selected.id,
        class_id: newPlan.class_id || null,
        plan_type: newPlan.plan_type,
        total_amount: parseFloat(newPlan.total_amount) || 0,
        monthly_amount: newPlan.monthly_amount ? parseFloat(newPlan.monthly_amount) : null,
        start_date: newPlan.start_date,
        end_date: newPlan.end_date || null,
        access_until: newPlan.access_until || null,
        notes: newPlan.notes || null,
      }).select().single();
      if (planError) throw planError;

      if (plan) {
        const installments = parseInt(newPlan.installments) || 0;
        const monthlyAmt = parseFloat(newPlan.monthly_amount) || parseFloat(newPlan.total_amount) || 0;

        if (newPlan.plan_type === 'monthly' && installments > 0 && newPlan.first_due_date) {
          const { error: rpcError } = await supabase.rpc('generate_payment_installments', {
            p_plan_id: plan.id,
            p_installments: installments,
            p_first_due_date: newPlan.first_due_date,
            p_monthly_amount: monthlyAmt,
            p_day_of_month: newPlan.day_of_month ? parseInt(newPlan.day_of_month) : null,
          });
          if (rpcError) throw rpcError;
        } else if (newPlan.plan_type === 'full' && newPlan.first_due_date) {
          const { error: payError } = await supabase.from('student_payments').insert({
            plan_id: plan.id,
            student_id: selected.id,
            amount: parseFloat(newPlan.total_amount) || 0,
            due_date: newPlan.first_due_date,
            status: 'open',
          });
          if (payError) throw payError;
        }
        addToast('success', 'Zahlungsplan angelegt');
        setShowNewPlan(false);
        setNewPlan({ class_id: '', plan_type: 'monthly', total_amount: '', monthly_amount: '', start_date: today(), end_date: '', access_until: '', notes: '', installments: '10', first_due_date: today(), day_of_month: '1' });
        load();
        setSelected(prev => prev ? { ...prev, plan_id: plan.id, plan_status: 'active' } : null);
        loadPayments(plan.id);
      }
    } catch (err) {
      if (import.meta.env.DEV) console.error('createPlan error:', err);
      addToast('error', 'Zahlungsplan konnte nicht angelegt werden.');
    } finally {
      setSaving(false);
    }
  };

  // ─── Add single payment ───────────────────────────────────────────────────

  const addPayment = async () => {
    if (!selected?.plan_id) return;
    if (!newPayment.due_date) {
      addToast('error', 'Bitte Fälligkeitsdatum angeben.');
      return;
    }
    setSaving(true);
    try {
      const { error } = await supabase.from('student_payments').insert({
        plan_id: selected.plan_id,
        student_id: selected.id,
        amount: parseFloat(newPayment.amount) || 0,
        due_date: newPayment.due_date,
        status: newPayment.status,
        payment_method: newPayment.payment_method || null,
        invoice_url: newPayment.invoice_url || null,
        notes: newPayment.notes || null,
      });
      if (error) throw error;
      addToast('success', 'Zahlung hinzugefügt');
      setShowNewPayment(false);
      setNewPayment({ amount: '', due_date: '', status: 'open', payment_method: '', invoice_url: '', notes: '' });
      loadPayments(selected.plan_id);
      load();
    } catch (err) {
      if (import.meta.env.DEV) console.error('addPayment error:', err);
      addToast('error', 'Zahlung konnte nicht hinzugefügt werden.');
    } finally {
      setSaving(false);
    }
  };

  // ─── Update payment field ─────────────────────────────────────────────────

  const updatePayment = async (id: string, fields: Partial<Payment>) => {
    try {
      const { error } = await supabase.from('student_payments').update(fields).eq('id', id);
      if (error) throw error;
      setPayments(prev => prev.map(p => p.id === id ? { ...p, ...fields } : p));
      load();
    } catch (err) {
      if (import.meta.env.DEV) console.error('updatePayment error:', err);
      addToast('error', 'Zahlung konnte nicht aktualisiert werden.');
      // Revert optimistic update
      if (selected?.plan_id) loadPayments(selected.plan_id);
    }
  };

  // ─── Quick actions ────────────────────────────────────────────────────────

  const flash = (id: string) => {
    setFlashId(id);
    setTimeout(() => setFlashId(null), 1400);
  };

  const quickPay = async (pay: Payment) => {
    const t = today();
    await updatePayment(pay.id, { status: 'paid', paid_date: t });
    if (selected?.id) {
      await supabase
        .from('portal_class_members')
        .update({ is_active: true })
        .eq('student_id', selected.id);
    }
    flash(pay.id);
    // Bewusst KEINE automatische Rechnung: Der Admin entscheidet danach selbst,
    // ob er über "Erstellen" generiert oder ein externes PDF hochlädt.
    addToast('success', 'Zahlung verbucht — Rechnung über „Erstellen" generieren oder per Upload hinzufügen');
  };

  const quickOverdue = async (pay: Payment) => {
    await updatePayment(pay.id, { status: 'overdue' });
    flash(pay.id);
  };

  const deletePayment = (id: string) => {
    setConfirmAction({
      label: 'Zahlung wirklich löschen?',
      onConfirm: async () => {
        try {
          const { error } = await supabase.from('student_payments').delete().eq('id', id);
          if (error) throw error;
          setPayments(prev => prev.filter(p => p.id !== id));
          addToast('success', 'Zahlung gelöscht');
          load();
        } catch (err) {
          if (import.meta.env.DEV) console.error('deletePayment error:', err);
          addToast('error', 'Zahlung konnte nicht gelöscht werden.');
        }
      },
    });
  };

  const deletePlan = () => {
    if (!selected?.plan_id) return;
    setConfirmAction({
      label: 'Zahlungsplan und alle zugehörigen Zahlungen wirklich löschen?',
      onConfirm: async () => {
        try {
          const { error } = await supabase.from('student_payment_plans').delete().eq('id', selected!.plan_id!);
          if (error) throw error;
          addToast('success', 'Zahlungsplan gelöscht');
          setSelected(null);
          load();
        } catch (err) {
          if (import.meta.env.DEV) console.error('deletePlan error:', err);
          addToast('error', 'Zahlungsplan konnte nicht gelöscht werden.');
        }
      },
    });
  };

  // ─── Save invoice URL ─────────────────────────────────────────────────────

  const saveInvoice = async (id: string) => {
    await updatePayment(id, { invoice_url: invoiceInput || null });
    setEditingInvoice(null);
  };

  const cancelInvoiceEdit = () => {
    setEditingInvoice(null);
    setInvoiceInput('');
  };

  // ─── Generierte Rechnungen (PDF) ──────────────────────────────────────────

  const invoiceMsg = (err: unknown, fallback: string) => {
    const m = err instanceof Error ? err.message : '';
    return m ? `${fallback}: ${m}` : fallback;
  };

  const createInvoice = async (paymentId: string) => {
    if (invoiceBusy) return;
    setInvoiceBusy(paymentId);
    try {
      const { data, error } = await supabase.functions.invoke('generate-invoice', {
        body: { payment_id: paymentId },
      });
      if (error || data?.error) throw new Error(data?.error ?? 'Serverfehler');
      addToast('success', `Rechnung ${data.invoice.invoice_number} erstellt`);
      if (data.warning) addToast('error', data.warning);
      if (selected) loadInvoices(selected.id);
    } catch (err) {
      if (import.meta.env.DEV) console.error('createInvoice error:', err);
      addToast('error', invoiceMsg(err, 'Rechnung konnte nicht erstellt werden'));
    } finally {
      setInvoiceBusy(null);
    }
  };

  const regenerateInvoicePdf = async (invoiceId: string) => {
    if (invoiceBusy) return false;
    setInvoiceBusy(invoiceId);
    try {
      const { data, error } = await supabase.functions.invoke('generate-invoice', {
        body: { invoice_id: invoiceId },
      });
      if (error || data?.error) throw new Error(data?.error ?? 'Serverfehler');
      if (selected) loadInvoices(selected.id);
      return true;
    } catch (err) {
      if (import.meta.env.DEV) console.error('regenerateInvoice error:', err);
      addToast('error', invoiceMsg(err, 'PDF konnte nicht erstellt werden'));
      return false;
    } finally {
      setInvoiceBusy(null);
    }
  };

  const downloadInvoice = async (inv: Invoice) => {
    if (!inv.pdf_path) return;
    try {
      await downloadInvoicePdf(inv.pdf_path, inv.invoice_number);
    } catch (err) {
      if (import.meta.env.DEV) console.error('downloadInvoice error:', err);
      addToast('error', 'PDF konnte nicht heruntergeladen werden.');
    }
  };

  const openInvoiceEdit = (inv: Invoice) => {
    setEditInvoiceRow(inv);
    setInvoiceForm({
      recipient_name: inv.recipient_name,
      recipient_street: inv.recipient_street ?? '',
      recipient_postal_code: inv.recipient_postal_code ?? '',
      recipient_city: inv.recipient_city ?? '',
      recipient_email: inv.recipient_email ?? '',
      description: inv.description,
      service_period: inv.service_period ?? '',
      amount: String(inv.amount),
      issued_at: inv.issued_at,
      notes: inv.notes ?? '',
    });
  };

  const saveInvoiceEdit = async () => {
    if (!editInvoiceRow) return;
    const amount = parseFloat(invoiceForm.amount.replace(',', '.'));
    if (Number.isNaN(amount) || amount <= 0) {
      addToast('error', 'Bitte einen gültigen Betrag angeben.');
      return;
    }
    setSaving(true);
    try {
      const { error } = await supabase.from('invoices').update({
        recipient_name: invoiceForm.recipient_name.trim() || editInvoiceRow.recipient_name,
        recipient_street: invoiceForm.recipient_street.trim() || null,
        recipient_postal_code: invoiceForm.recipient_postal_code.trim() || null,
        recipient_city: invoiceForm.recipient_city.trim() || null,
        recipient_email: invoiceForm.recipient_email.trim() || null,
        description: invoiceForm.description.trim() || editInvoiceRow.description,
        service_period: invoiceForm.service_period.trim() || null,
        amount,
        issued_at: invoiceForm.issued_at || editInvoiceRow.issued_at,
        notes: invoiceForm.notes.trim() || null,
      }).eq('id', editInvoiceRow.id);
      if (error) throw error;
      if (editInvoiceRow.source === 'uploaded') {
        // Extern erstelltes PDF bleibt unangetastet — nur Metadaten gespeichert
        addToast('success', `Rechnung ${editInvoiceRow.invoice_number} aktualisiert`);
        if (selected) loadInvoices(selected.id);
      } else {
        const ok = await regenerateInvoicePdf(editInvoiceRow.id);
        if (ok) addToast('success', `Rechnung ${editInvoiceRow.invoice_number} aktualisiert, PDF neu erstellt`);
      }
      setEditInvoiceRow(null);
    } catch (err) {
      if (import.meta.env.DEV) console.error('saveInvoiceEdit error:', err);
      addToast('error', 'Rechnung konnte nicht gespeichert werden.');
    } finally {
      setSaving(false);
    }
  };

  const cancelGeneratedInvoice = (inv: Invoice) => {
    setConfirmAction({
      label: `Rechnung ${inv.invoice_number} wirklich stornieren? Die Nummer bleibt vergeben; für die Zahlung kann eine neue Rechnung erstellt werden.`,
      confirmLabel: 'Stornieren',
      onConfirm: async () => {
        try {
          const { error } = await supabase.from('invoices')
            .update({ status: 'cancelled' })
            .eq('id', inv.id);
          if (error) throw error;
          // PDF mit Storno-Kennzeichnung neu rendern
          await regenerateInvoicePdf(inv.id);
          addToast('success', `Rechnung ${inv.invoice_number} storniert`);
          setEditInvoiceRow(null);
          if (selected) loadInvoices(selected.id);
        } catch (err) {
          if (import.meta.env.DEV) console.error('cancelGeneratedInvoice error:', err);
          addToast('error', 'Rechnung konnte nicht storniert werden.');
        }
      },
    });
  };

  // ─── Extern erstellte Rechnung hochladen (z. B. sevdesk) ──────────────────

  const submitUploadInvoice = async () => {
    if (!uploadForPayment || !uploadFile || !selected) return;
    const number = uploadNumber.trim();
    if (!number) {
      addToast('error', 'Bitte die Rechnungsnummer aus dem externen System angeben.');
      return;
    }
    setUploadBusy(true);
    try {
      const safeName = number.replace(/[^A-Za-z0-9._-]/g, '_');
      const path = `${selected.id}/${safeName}.pdf`;
      const { error: upErr } = await supabase.storage
        .from('invoices')
        .upload(path, uploadFile, { contentType: 'application/pdf', upsert: true });
      if (upErr) throw upErr;

      const { error: insErr } = await supabase.from('invoices').insert({
        invoice_number: number,
        payment_id: uploadForPayment.id,
        plan_id: uploadForPayment.plan_id,
        student_id: selected.id,
        recipient_name: `${selected.first_name} ${selected.last_name}`.trim(),
        description: 'Kursgebühr (extern erstellte Rechnung)',
        amount: uploadForPayment.amount,
        currency: uploadForPayment.currency ?? 'EUR',
        paid_date: uploadForPayment.paid_date,
        pdf_path: path,
        source: 'uploaded',
      });
      if (insErr) {
        if ((insErr as { code?: string }).code === '23505') {
          throw new Error(`Die Rechnungsnummer ${number} existiert bereits.`);
        }
        throw insErr;
      }
      addToast('success', `Rechnung ${number} hochgeladen`);
      setUploadForPayment(null);
      setUploadNumber('');
      setUploadFile(null);
      loadInvoices(selected.id);
    } catch (err) {
      if (import.meta.env.DEV) console.error('submitUploadInvoice error:', err);
      addToast('error', invoiceMsg(err, 'Rechnung konnte nicht hochgeladen werden'));
    } finally {
      setUploadBusy(false);
    }
  };

  // ─── Rechnungs-Einstellungen ──────────────────────────────────────────────

  const handleLogoUpload = async (file: File) => {
    const ext = file.name.toLowerCase().endsWith('.png') ? 'png'
      : /\.jpe?g$/i.test(file.name) ? 'jpg' : null;
    if (!ext) {
      addToast('error', 'Bitte ein PNG- oder JPG-Logo hochladen.');
      return;
    }
    setLogoUploading(true);
    try {
      const path = `settings/logo-${Date.now()}.${ext}`;
      const { error } = await supabase.storage
        .from('invoices')
        .upload(path, file, { contentType: ext === 'png' ? 'image/png' : 'image/jpeg', upsert: true });
      if (error) throw error;
      setSettingsForm(f => ({ ...f, logo_path: path }));
      addToast('success', 'Logo hochgeladen — mit „Speichern" übernehmen');
    } catch (err) {
      if (import.meta.env.DEV) console.error('handleLogoUpload error:', err);
      addToast('error', 'Logo konnte nicht hochgeladen werden.');
    } finally {
      setLogoUploading(false);
    }
  };

  const openInvoiceSettings = async () => {
    setShowInvoiceSettings(true);
    const { data } = await supabase.from('invoice_settings').select('*').eq('id', true).maybeSingle();
    if (data) {
      setSettingsForm({
        issuer_name: data.issuer_name ?? '',
        issuer_street: data.issuer_street ?? '',
        issuer_postal_code: data.issuer_postal_code ?? '',
        issuer_city: data.issuer_city ?? '',
        issuer_email: data.issuer_email ?? '',
        tax_number: data.tax_number ?? '',
        small_business: data.small_business ?? true,
        tax_rate: String(data.tax_rate ?? 19),
        footer_text: data.footer_text ?? '',
        logo_path: data.logo_path ?? '',
        accent_color: data.accent_color ?? '#970000',
        intro_text: data.intro_text ?? '',
        closing_text: data.closing_text ?? '',
        bank_account_holder: data.bank_account_holder ?? '',
        bank_iban: data.bank_iban ?? '',
        bank_bic: data.bank_bic ?? '',
        bank_name: data.bank_name ?? '',
      });
    }
  };

  const saveInvoiceSettings = async () => {
    const taxRate = parseFloat(settingsForm.tax_rate.replace(',', '.'));
    setSettingsSaving(true);
    try {
      const { error } = await supabase.from('invoice_settings').upsert({
        id: true,
        issuer_name: settingsForm.issuer_name.trim() || 'Arabicroots',
        issuer_street: settingsForm.issuer_street.trim(),
        issuer_postal_code: settingsForm.issuer_postal_code.trim(),
        issuer_city: settingsForm.issuer_city.trim(),
        issuer_email: settingsForm.issuer_email.trim(),
        tax_number: settingsForm.tax_number.trim(),
        small_business: settingsForm.small_business,
        tax_rate: Number.isNaN(taxRate) ? 19 : taxRate,
        footer_text: settingsForm.footer_text.trim(),
        logo_path: settingsForm.logo_path,
        accent_color: /^#[0-9a-f]{6}$/i.test(settingsForm.accent_color) ? settingsForm.accent_color : '#970000',
        intro_text: settingsForm.intro_text.trim(),
        closing_text: settingsForm.closing_text.trim(),
        bank_account_holder: settingsForm.bank_account_holder.trim(),
        bank_iban: settingsForm.bank_iban.trim(),
        bank_bic: settingsForm.bank_bic.trim(),
        bank_name: settingsForm.bank_name.trim(),
      });
      if (error) throw error;
      addToast('success', 'Rechnungs-Einstellungen gespeichert');
      setShowInvoiceSettings(false);
    } catch (err) {
      if (import.meta.env.DEV) console.error('saveInvoiceSettings error:', err);
      addToast('error', 'Einstellungen konnten nicht gespeichert werden.');
    } finally {
      setSettingsSaving(false);
    }
  };

  // ─── Update overdue ───────────────────────────────────────────────────────

  const runUpdateOverdue = async () => {
    setUpdatingOverdue(true);
    try {
      // Volle Durchsetzung: überfällig markieren + Zugänge nach Karenz sperren
      const { data, error } = await supabase.rpc('enforce_payment_access');
      if (error) throw error;
      const count = typeof data === 'number' ? data : 0;
      setOverdueMsg(count === 0 ? 'Keine neuen überfälligen Zahlungen — Zugänge geprüft' : `${count} Zahlung(en) jetzt überfällig — Zugänge geprüft`);
      setTimeout(() => setOverdueMsg(null), 4000);
      load();
      if (selected?.plan_id) loadPayments(selected.plan_id);
    } catch (err) {
      if (import.meta.env.DEV) console.error('runUpdateOverdue error:', err);
      addToast('error', 'Fälligkeiten konnten nicht aktualisiert werden.');
    } finally {
      setUpdatingOverdue(false);
    }
  };

  // ─── CSV Export with month picker ─────────────────────────────────────────

  const exportCSV = async () => {
    const [year, month] = csvMonth.split('-').map(Number);
    const firstDay = new Date(year, month - 1, 1).toISOString().slice(0, 10);
    const lastDay = new Date(year, month, 0).toISOString().slice(0, 10);

    try {
      const { data: rows, error } = await supabase
        .from('student_payments')
        .select('*, plan:student_payment_plans(student_id, class_id)')
        .gte('due_date', firstDay)
        .lte('due_date', lastDay)
        .order('due_date');
      if (error) throw error;

      const header = 'Name,Kurs,Betrag,Fälligkeit,Status,Zahlungsart';
      const lines = (rows ?? []).map((r: { plan?: { student_id?: string; class_id?: string } | null; amount?: number; due_date?: string; status?: string; payment_method?: string | null }) => {
        const sid = r.plan?.student_id ?? '';
        const cid = r.plan?.class_id ?? '';
        const name = studentMap[sid] ?? sid;
        const kurs = classMap[cid] ?? '–';
        const betrag = ((r.amount ?? 0) as number).toFixed(2);
        const faelligkeit = r.due_date ?? '';
        const status = STATUS_LABEL[r.status ?? ''] ?? (r.status ?? '');
        const methode = METHOD_LABEL[r.payment_method ?? ''] ?? '–';
        return `"${name}","${kurs}","${betrag}","${faelligkeit}","${status}","${methode}"`;
      });

      const csv = [header, ...lines].join('\n');
      const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `zahlungen-${csvMonth}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      if (import.meta.env.DEV) console.error('exportCSV error:', err);
      addToast('error', 'CSV-Export fehlgeschlagen.');
    }
  };

  // ─── Styles ───────────────────────────────────────────────────────────────

  const inputCls = 'w-full px-3 py-2 text-sm border border-[#ddd6cf] rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#970000]/20';
  const labelCls = 'block text-xs font-medium text-[#6b6b6b] mb-1';

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="space-y-5">
      {/* Toast notifications */}
      <div className="fixed top-4 right-4 z-[100] space-y-2 pointer-events-none">
        {toasts.map(t => (
          <div
            key={t.id}
            className={`flex items-center gap-2 px-4 py-3 rounded-2xl shadow-lg text-sm font-medium pointer-events-auto transition-all animate-in slide-in-from-right ${
              t.type === 'success'
                ? 'bg-white border border-green-200 text-green-800'
                : 'bg-white border border-red-200 text-red-800'
            }`}
          >
            {t.type === 'success'
              ? <CheckCircle className="h-4 w-4 text-green-600 shrink-0" />
              : <AlertCircle className="h-4 w-4 text-red-600 shrink-0" />
            }
            {t.message}
          </div>
        ))}
      </div>

      {/* Confirm dialog */}
      {confirmAction && (
        <div className="fixed inset-0 bg-black/40 z-[200] flex items-center justify-center p-4">
          <div className="bg-white rounded-[24px] p-6 w-full max-w-sm shadow-xl">
            <p className="font-semibold text-[#1f1f1f] mb-4">{confirmAction.label}</p>
            <div className="flex gap-3">
              <button onClick={() => { confirmAction.onConfirm(); setConfirmAction(null); }} className="inline-flex items-center px-4 py-2 rounded-full bg-red-600 text-white hover:bg-red-700 transition-colors font-medium text-sm">{confirmAction.confirmLabel ?? 'Löschen'}</button>
              <button onClick={() => setConfirmAction(null)} className="inline-flex items-center px-4 py-2 rounded-full border border-[#d9c9bd] text-[#5f5148] hover:bg-[#f8f3ef] transition-colors font-medium text-sm">Abbrechen</button>
            </div>
          </div>
        </div>
      )}

      {/* Rechnung bearbeiten */}
      {editInvoiceRow && (
        <div className="fixed inset-0 bg-black/40 z-[150] flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-[24px] p-6 w-full max-w-lg shadow-xl my-8">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="font-bold text-[#111827]">Rechnung bearbeiten</h3>
                <p className="text-xs text-[#6b6b6b] mt-0.5">
                  Nummer: <span className="font-semibold">{editInvoiceRow.invoice_number}</span> (nicht änderbar — fortlaufende Nummerierung)
                </p>
              </div>
              <button onClick={() => setEditInvoiceRow(null)} className="p-1 text-[#6b6b6b] hover:text-[#111827]">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className={labelCls}>Empfänger (Name)</label>
                <input value={invoiceForm.recipient_name} onChange={e => setInvoiceForm(f => ({ ...f, recipient_name: e.target.value }))} className={inputCls} />
              </div>
              <div className="col-span-2">
                <label className={labelCls}>Straße & Hausnummer</label>
                <input value={invoiceForm.recipient_street} onChange={e => setInvoiceForm(f => ({ ...f, recipient_street: e.target.value }))} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>PLZ</label>
                <input value={invoiceForm.recipient_postal_code} onChange={e => setInvoiceForm(f => ({ ...f, recipient_postal_code: e.target.value }))} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Ort</label>
                <input value={invoiceForm.recipient_city} onChange={e => setInvoiceForm(f => ({ ...f, recipient_city: e.target.value }))} className={inputCls} />
              </div>
              <div className="col-span-2">
                <label className={labelCls}>E-Mail</label>
                <input type="email" value={invoiceForm.recipient_email} onChange={e => setInvoiceForm(f => ({ ...f, recipient_email: e.target.value }))} className={inputCls} />
              </div>
              <div className="col-span-2">
                <label className={labelCls}>Leistungsbeschreibung</label>
                <input value={invoiceForm.description} onChange={e => setInvoiceForm(f => ({ ...f, description: e.target.value }))} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Leistungszeitraum</label>
                <input value={invoiceForm.service_period} onChange={e => setInvoiceForm(f => ({ ...f, service_period: e.target.value }))} placeholder="z.B. 07/2026" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Betrag (€)</label>
                <input value={invoiceForm.amount} onChange={e => setInvoiceForm(f => ({ ...f, amount: e.target.value }))} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Rechnungsdatum</label>
                <input type="date" value={invoiceForm.issued_at} onChange={e => setInvoiceForm(f => ({ ...f, issued_at: e.target.value }))} className={inputCls} />
              </div>
              <div className="col-span-2">
                <label className={labelCls}>Vermerk auf der Rechnung (optional)</label>
                <input value={invoiceForm.notes} onChange={e => setInvoiceForm(f => ({ ...f, notes: e.target.value }))} className={inputCls} />
              </div>
            </div>

            <p className="text-[11px] text-[#9b9490] mt-3 leading-relaxed">
              {editInvoiceRow.source === 'uploaded'
                ? 'Extern erstellte Rechnung: Das hochgeladene PDF bleibt unverändert, hier werden nur die Verzeichnisdaten gepflegt.'
                : 'Hinweis: Wurde die Rechnung bereits an den Schüler herausgegeben, ist es formal sauberer, sie zu stornieren und eine neue zu erstellen.'}
            </p>

            <div className="flex flex-wrap items-center gap-2 mt-4">
              <button
                onClick={saveInvoiceEdit}
                disabled={saving || !!invoiceBusy}
                className="px-4 py-2 bg-[#970000] text-white rounded-full text-sm font-medium hover:bg-[#7a0000] disabled:opacity-50"
              >
                {saving || invoiceBusy
                  ? 'Speichern…'
                  : editInvoiceRow.source === 'uploaded' ? 'Speichern' : 'Speichern & PDF neu erstellen'}
              </button>
              {editInvoiceRow.pdf_path && (
                <button
                  onClick={() => downloadInvoice(editInvoiceRow)}
                  className="inline-flex items-center gap-1.5 px-4 py-2 border border-[#d9c9bd] rounded-full text-sm text-[#6b6b6b] hover:bg-[#faf7f5]"
                >
                  <Download className="h-3.5 w-3.5" />
                  PDF herunterladen
                </button>
              )}
              <button
                onClick={() => cancelGeneratedInvoice(editInvoiceRow)}
                className="inline-flex items-center gap-1.5 px-4 py-2 border border-red-200 text-red-600 rounded-full text-sm font-medium hover:bg-red-50 ml-auto"
              >
                Stornieren
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Externe Rechnung hochladen (z. B. sevdesk) */}
      {uploadForPayment && (
        <div className="fixed inset-0 bg-black/40 z-[150] flex items-center justify-center p-4">
          <div className="bg-white rounded-[24px] p-6 w-full max-w-md shadow-xl">
            <div className="flex items-start justify-between mb-1">
              <h3 className="font-bold text-[#111827]">Rechnung hochladen</h3>
              <button onClick={() => setUploadForPayment(null)} className="p-1 text-[#6b6b6b] hover:text-[#111827]">
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="text-xs text-[#6b6b6b] mb-4">
              Für extern erstellte Rechnungen (z. B. aus sevdesk). Das PDF wird dem Schüler
              zum Download bereitgestellt und hier nicht verändert. Zahlung: {fmtAmount(uploadForPayment.amount)},
              fällig {fmt(uploadForPayment.due_date)}.
            </p>
            <div className="space-y-3">
              <div>
                <label className={labelCls}>Rechnungsnummer (aus dem externen System)</label>
                <input value={uploadNumber} onChange={e => setUploadNumber(e.target.value)} placeholder="z.B. RE-1042" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>PDF-Datei</label>
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={e => setUploadFile(e.target.files?.[0] ?? null)}
                  className="block w-full text-sm text-[#6b6b6b] file:mr-3 file:px-3 file:py-1.5 file:rounded-full file:border file:border-[#d9c9bd] file:bg-white file:text-xs file:font-medium file:text-[#6b6b6b] hover:file:bg-[#faf7f5]"
                />
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button
                onClick={submitUploadInvoice}
                disabled={uploadBusy || !uploadFile || !uploadNumber.trim()}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#970000] text-white rounded-full text-sm font-medium hover:bg-[#7a0000] disabled:opacity-50"
              >
                {uploadBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                {uploadBusy ? 'Hochladen…' : 'Hochladen'}
              </button>
              <button onClick={() => setUploadForPayment(null)} className="px-4 py-2 border border-[#d9c9bd] rounded-full text-sm text-[#6b6b6b] hover:bg-[#faf7f5]">
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rechnungs-Einstellungen (Ausstellerdaten) */}
      {showInvoiceSettings && (
        <div className="fixed inset-0 bg-black/40 z-[150] flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-[24px] p-6 w-full max-w-lg shadow-xl my-8">
            <div className="flex items-start justify-between mb-1">
              <h3 className="font-bold text-[#111827]">Rechnungs-Einstellungen</h3>
              <button onClick={() => setShowInvoiceSettings(false)} className="p-1 text-[#6b6b6b] hover:text-[#111827]">
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="text-xs text-[#6b6b6b] mb-4">
              Diese Angaben erscheinen als Aussteller auf allen erstellten Rechnungen (§ 14 UStG).
            </p>

            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className={labelCls}>Name / Firma</label>
                <input value={settingsForm.issuer_name} onChange={e => setSettingsForm(f => ({ ...f, issuer_name: e.target.value }))} className={inputCls} />
              </div>
              <div className="col-span-2">
                <label className={labelCls}>Straße & Hausnummer</label>
                <input value={settingsForm.issuer_street} onChange={e => setSettingsForm(f => ({ ...f, issuer_street: e.target.value }))} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>PLZ</label>
                <input value={settingsForm.issuer_postal_code} onChange={e => setSettingsForm(f => ({ ...f, issuer_postal_code: e.target.value }))} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Ort</label>
                <input value={settingsForm.issuer_city} onChange={e => setSettingsForm(f => ({ ...f, issuer_city: e.target.value }))} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>E-Mail</label>
                <input type="email" value={settingsForm.issuer_email} onChange={e => setSettingsForm(f => ({ ...f, issuer_email: e.target.value }))} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Steuernummer / USt-IdNr.</label>
                <input value={settingsForm.tax_number} onChange={e => setSettingsForm(f => ({ ...f, tax_number: e.target.value }))} className={inputCls} />
              </div>
              <div className="col-span-2">
                <label className={labelCls}>Kleinunternehmerregelung (§ 19 UStG)</label>
                <button
                  onClick={() => setSettingsForm(f => ({ ...f, small_business: !f.small_business }))}
                  className={`flex items-center gap-2 px-3 py-2 rounded-2xl border text-sm font-medium transition-colors ${settingsForm.small_business ? 'bg-green-50 border-green-200 text-green-700' : 'border-[#ddd6cf] text-[#6b6b6b]'}`}
                >
                  {settingsForm.small_business ? <ToggleRight className="h-5 w-5" /> : <ToggleLeft className="h-5 w-5" />}
                  {settingsForm.small_business
                    ? 'Aktiv — keine USt, Hinweis auf der Rechnung'
                    : 'Inaktiv — USt wird ausgewiesen'}
                </button>
              </div>
              {!settingsForm.small_business && (
                <div>
                  <label className={labelCls}>USt-Satz (%)</label>
                  <input value={settingsForm.tax_rate} onChange={e => setSettingsForm(f => ({ ...f, tax_rate: e.target.value }))} className={inputCls} />
                </div>
              )}
              <div className="col-span-2">
                <label className={labelCls}>Fußzeile (z.B. Bankverbindung, Kontakt — mehrzeilig möglich)</label>
                <textarea value={settingsForm.footer_text} onChange={e => setSettingsForm(f => ({ ...f, footer_text: e.target.value }))} rows={2} className={`${inputCls} resize-none`} />
              </div>

              {/* Bankverbindung */}
              <div className="col-span-2 border-t border-[#eadfd6] pt-3 mt-1">
                <p className="text-xs font-semibold text-[#111827] mb-1">Bankverbindung (Überweisung)</p>
                <p className="text-[11px] text-[#9b9490]">
                  Wird Schülern auf der Sperr- und Zahlungsseite als Überweisungsmöglichkeit angezeigt.
                  Leer lassen, um keine Bankdaten anzuzeigen.
                </p>
              </div>
              <div>
                <label className={labelCls}>Kontoinhaber</label>
                <input value={settingsForm.bank_account_holder} onChange={e => setSettingsForm(f => ({ ...f, bank_account_holder: e.target.value }))} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Bank</label>
                <input value={settingsForm.bank_name} onChange={e => setSettingsForm(f => ({ ...f, bank_name: e.target.value }))} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>IBAN</label>
                <input value={settingsForm.bank_iban} onChange={e => setSettingsForm(f => ({ ...f, bank_iban: e.target.value }))} placeholder="DE.." className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>BIC</label>
                <input value={settingsForm.bank_bic} onChange={e => setSettingsForm(f => ({ ...f, bank_bic: e.target.value }))} className={inputCls} />
              </div>

              {/* Design */}
              <div className="col-span-2 border-t border-[#eadfd6] pt-3 mt-1">
                <p className="text-xs font-semibold text-[#111827] mb-2">Design</p>
              </div>
              <div>
                <label className={labelCls}>Logo (PNG/JPG, oben links)</label>
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    accept="image/png,image/jpeg"
                    onChange={e => { const f = e.target.files?.[0]; if (f) handleLogoUpload(f); }}
                    className="block w-full text-xs text-[#6b6b6b] file:mr-2 file:px-3 file:py-1.5 file:rounded-full file:border file:border-[#d9c9bd] file:bg-white file:text-xs file:font-medium file:text-[#6b6b6b] hover:file:bg-[#faf7f5]"
                  />
                  {logoUploading && <Loader2 className="h-4 w-4 animate-spin text-[#6b6b6b] shrink-0" />}
                </div>
                {settingsForm.logo_path && (
                  <button
                    onClick={() => setSettingsForm(f => ({ ...f, logo_path: '' }))}
                    className="mt-1 text-[11px] text-red-600 hover:underline"
                  >
                    Logo entfernen
                  </button>
                )}
              </div>
              <div>
                <label className={labelCls}>Akzentfarbe</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={/^#[0-9a-f]{6}$/i.test(settingsForm.accent_color) ? settingsForm.accent_color : '#970000'}
                    onChange={e => setSettingsForm(f => ({ ...f, accent_color: e.target.value }))}
                    className="h-9 w-12 rounded-lg border border-[#ddd6cf] cursor-pointer"
                  />
                  <input
                    value={settingsForm.accent_color}
                    onChange={e => setSettingsForm(f => ({ ...f, accent_color: e.target.value }))}
                    className={inputCls}
                    placeholder="#970000"
                  />
                </div>
              </div>
              <div className="col-span-2">
                <label className={labelCls}>Einleitungstext (über der Position, optional)</label>
                <textarea value={settingsForm.intro_text} onChange={e => setSettingsForm(f => ({ ...f, intro_text: e.target.value }))} rows={2} placeholder="z.B. wir erlauben uns, folgende Leistung in Rechnung zu stellen:" className={`${inputCls} resize-none`} />
              </div>
              <div className="col-span-2">
                <label className={labelCls}>Schlusstext (unter der Summe, optional)</label>
                <textarea value={settingsForm.closing_text} onChange={e => setSettingsForm(f => ({ ...f, closing_text: e.target.value }))} rows={2} placeholder="z.B. Vielen Dank für dein Vertrauen!" className={`${inputCls} resize-none`} />
              </div>
              <p className="col-span-2 text-[11px] text-[#9b9490]">
                Änderungen wirken auf neu erstellte PDFs. Bestehende Rechnungen kannst du
                über „Bearbeiten → Speichern & PDF neu erstellen" aktualisieren.
              </p>
            </div>

            <div className="flex gap-2 mt-4">
              <button
                onClick={saveInvoiceSettings}
                disabled={settingsSaving}
                className="px-4 py-2 bg-[#970000] text-white rounded-full text-sm font-medium hover:bg-[#7a0000] disabled:opacity-50"
              >
                {settingsSaving ? 'Speichern…' : 'Speichern'}
              </button>
              <button onClick={() => setShowInvoiceSettings(false)} className="px-4 py-2 border border-[#d9c9bd] rounded-full text-sm text-[#6b6b6b] hover:bg-[#faf7f5]">
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header row */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-xl font-bold text-[#111827]">Zahlung & Zugang</h2>
        <div className="flex flex-wrap items-center gap-2">
          {overdueMsg && (
            <span className="text-xs text-green-700 bg-green-50 border border-green-200 px-3 py-1.5 rounded-full">{overdueMsg}</span>
          )}
          <button
            onClick={openInvoiceSettings}
            className="inline-flex items-center gap-1.5 px-3 py-2 border border-[#d9c9bd] rounded-full text-xs font-medium text-[#6b6b6b] hover:bg-[#faf7f5]"
          >
            <Settings className="h-3.5 w-3.5" />
            Rechnungsdaten
          </button>
          <button
            onClick={runUpdateOverdue}
            disabled={updatingOverdue}
            className="inline-flex items-center gap-1.5 px-3 py-2 border border-[#d9c9bd] rounded-full text-xs font-medium text-[#6b6b6b] hover:bg-[#faf7f5] disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${updatingOverdue ? 'animate-spin' : ''}`} />
            Fälligkeiten aktualisieren
          </button>
          {/* CSV Export with month picker */}
          <div className="flex items-center gap-1 border border-[#d9c9bd] rounded-full overflow-hidden">
            <input
              type="month"
              value={csvMonth}
              onChange={e => setCsvMonth(e.target.value)}
              className="text-xs text-[#6b6b6b] bg-transparent px-3 py-2 outline-none"
            />
            <button
              onClick={exportCSV}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-[#6b6b6b] hover:bg-[#faf7f5] border-l border-[#d9c9bd]"
            >
              <Download className="h-3.5 w-3.5" />
              CSV
            </button>
          </div>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Aktive Pläne', value: stats.activePlans, color: 'text-green-700', bg: 'bg-green-50 border-green-200', onClick: () => { setOverdueFilter(false); setSearch(''); } },
          { label: 'Offene Zahlungen', value: stats.openPayments, color: 'text-yellow-700', bg: 'bg-yellow-50 border-yellow-200', onClick: () => { setOverdueFilter(false); setSearch(''); } },
          { label: 'Überfällig', value: stats.overdueCount, color: 'text-red-700', bg: 'bg-red-50 border-red-200', onClick: () => setOverdueFilter(v => !v), active: overdueFilter },
          { label: 'Einnahmen diesen Monat', value: `${stats.monthlyRevenue.toFixed(2)} €`, color: 'text-[#970000]', bg: 'bg-[#fff8f8] border-[#f3d0d0]', onClick: undefined },
        ].map(s => (
          <button
            key={s.label}
            onClick={s.onClick}
            disabled={!s.onClick}
            className={`text-left p-4 rounded-2xl border transition-all ${s.bg} ${s.onClick ? 'cursor-pointer hover:opacity-80' : 'cursor-default'} ${(s as { active?: boolean }).active ? 'ring-2 ring-offset-1 ring-red-400' : ''}`}
          >
            <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
            <p className="text-xs text-[#6b6b6b] mt-0.5">{s.label}</p>
          </button>
        ))}
      </div>

      {/* Suche + Filter */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6b6b6b]" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Schüler suchen…"
            className="w-full pl-9 pr-4 py-2 text-sm border border-[#ddd6cf] rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#970000]/20"
          />
        </div>
        <select
          value={classFilter}
          onChange={e => setClassFilter(e.target.value)}
          className="px-3 py-2 text-sm border border-[#ddd6cf] rounded-2xl bg-white focus:outline-none focus:ring-2 focus:ring-[#970000]/20"
        >
          <option value="">Alle Klassen</option>
          <option value="none">Ohne Klasse/Plan</option>
          {classes.map(c => (
            <option key={c.id} value={c.id}>{c.title}</option>
          ))}
        </select>
        {overdueFilter && (
          <button
            onClick={() => setOverdueFilter(false)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-100 text-red-700 rounded-full text-xs font-medium hover:bg-red-200"
          >
            <X className="h-3.5 w-3.5" /> Nur Überfällige
          </button>
        )}
        <span className="text-xs text-[#9b9490] ml-auto">{filtered.length} Schüler</span>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#970000]" />
        </div>
      ) : (
        <div className="bg-white rounded-[24px] border border-[#eadfd6] shadow-[0_10px_35px_rgba(0,0,0,0.05)] overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#faf7f5] border-b border-[#eadfd6]">
              <tr>
                {['Schüler', 'Klasse', 'Zahlungsplan', 'Nächste Fälligkeit', 'Offen / Überfällig', 'Zugriff bis', 'Freischaltung'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-[#6b6b6b] uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#f3ede8]">
              {filtered.map(s => (
                <tr
                  key={s.id}
                  onClick={() => openModal(s)}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openModal(s); } }}
                  tabIndex={0}
                  role="button"
                  aria-label={`${s.first_name} ${s.last_name} öffnen`}
                  className="hover:bg-[#faf7f5] cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[#970000]/20"
                >
                  <td className="px-4 py-3 font-medium text-[#111827]">{s.first_name} {s.last_name}</td>
                  <td className="px-4 py-3 text-[#6b6b6b]">{s.class_title ?? '–'}</td>
                  <td className="px-4 py-3">
                    {s.plan_status ? (
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${PLAN_STATUS_COLOR[s.plan_status] ?? ''}`}>
                        {PLAN_STATUS_LABEL[s.plan_status] ?? s.plan_status}
                      </span>
                    ) : <span className="text-[#6b6b6b]">–</span>}
                  </td>
                  <td className="px-4 py-3 text-[#6b6b6b]">{fmt(s.next_due)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      {s.open_count > 0 && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                          {s.open_count} offen
                        </span>
                      )}
                      {s.overdue_count > 0 && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                          <AlertCircle className="h-3 w-3" />{s.overdue_count}
                        </span>
                      )}
                      {s.open_count === 0 && s.overdue_count === 0 && <span className="text-[#6b6b6b]">–</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-[#6b6b6b]">{fmt(s.access_until)}</td>
                  <td className="px-4 py-3">
                    {s.manual_override ? (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700">
                        <CheckCircle className="h-3.5 w-3.5" /> Manuell
                      </span>
                    ) : <span className="text-[#6b6b6b]">–</span>}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-[#6b6b6b]">Keine Schüler gefunden</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Modal ── */}
      {selected && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div
            className="bg-white rounded-[24px] border border-[#eadfd6] shadow-[0_20px_60px_rgba(0,0,0,0.15)] w-full max-w-3xl max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-[#eadfd6]">
              <div>
                <h3 className="text-lg font-bold text-[#111827]">{selected.first_name} {selected.last_name}</h3>
                {selected.class_title && <p className="text-sm text-[#6b6b6b]">{selected.class_title}</p>}
              </div>
              <button onClick={() => setSelected(null)} className="p-2 rounded-xl hover:bg-[#faf7f5]">
                <X className="h-5 w-5 text-[#6b6b6b]" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* No plan */}
              {!selected.plan_id && !showNewPlan && (
                <div className="text-center py-6 border border-dashed border-[#ddd6cf] rounded-2xl">
                  <p className="text-[#6b6b6b] text-sm mb-3">Noch kein Zahlungsplan vorhanden</p>
                  <button onClick={() => setShowNewPlan(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-[#970000] text-white rounded-full text-sm font-medium hover:bg-[#7a0000]">
                    <Plus className="h-4 w-4" /> Zahlungsplan anlegen
                  </button>
                </div>
              )}

              {/* New plan form */}
              {showNewPlan && (
                <div className="border border-[#eadfd6] rounded-2xl p-4 space-y-3 bg-[#faf7f5]">
                  <h4 className="font-semibold text-[#111827] text-sm">Neuer Zahlungsplan</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>Klasse</label>
                      <select value={newPlan.class_id} onChange={e => setNewPlan(p => ({ ...p, class_id: e.target.value }))} className={inputCls}>
                        <option value="">Keine Klasse</option>
                        {classes.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className={labelCls}>Zahlungsart</label>
                      <select value={newPlan.plan_type} onChange={e => setNewPlan(p => ({ ...p, plan_type: e.target.value }))} className={inputCls}>
                        <option value="monthly">Ratenzahlung</option>
                        <option value="full">Einmalzahlung</option>
                      </select>
                    </div>
                    <div>
                      <label className={labelCls}>Gesamtbetrag (€)</label>
                      <input type="number" value={newPlan.total_amount} onChange={e => setNewPlan(p => ({ ...p, total_amount: e.target.value }))} className={inputCls} placeholder="0.00" />
                    </div>
                    {newPlan.plan_type === 'monthly' && (
                      <>
                        <div>
                          <label className={labelCls}>Rate pro Monat (€)</label>
                          <input type="number" value={newPlan.monthly_amount} onChange={e => setNewPlan(p => ({ ...p, monthly_amount: e.target.value }))} className={inputCls} placeholder="0.00" />
                        </div>
                        <div>
                          <label className={labelCls}>Anzahl Raten</label>
                          <input type="number" min="1" max="60" value={newPlan.installments} onChange={e => setNewPlan(p => ({ ...p, installments: e.target.value }))} className={inputCls} placeholder="10" />
                        </div>
                        <div>
                          <label className={labelCls}>Fälligkeitstag (1–28)</label>
                          <input type="number" min="1" max="28" value={newPlan.day_of_month} onChange={e => setNewPlan(p => ({ ...p, day_of_month: e.target.value }))} className={inputCls} placeholder="1" />
                        </div>
                      </>
                    )}
                    <div>
                      <label className={labelCls}>Erste Fälligkeit</label>
                      <input type="date" value={newPlan.first_due_date} onChange={e => setNewPlan(p => ({ ...p, first_due_date: e.target.value }))} className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>Startdatum</label>
                      <input type="date" value={newPlan.start_date} onChange={e => setNewPlan(p => ({ ...p, start_date: e.target.value }))} className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>Enddatum (optional)</label>
                      <input type="date" value={newPlan.end_date} onChange={e => setNewPlan(p => ({ ...p, end_date: e.target.value }))} className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>Zugriff bis (optional)</label>
                      <input type="date" value={newPlan.access_until} onChange={e => setNewPlan(p => ({ ...p, access_until: e.target.value }))} className={inputCls} />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Notizen</label>
                    <textarea value={newPlan.notes} onChange={e => setNewPlan(p => ({ ...p, notes: e.target.value }))} className={`${inputCls} resize-none`} rows={2} />
                  </div>
                  {newPlan.plan_type === 'monthly' && newPlan.installments && newPlan.monthly_amount && (
                    <p className="text-xs text-[#6b6b6b] bg-white border border-[#eadfd6] rounded-xl px-3 py-2">
                      Automatisch generiert: {newPlan.installments} Raten à {newPlan.monthly_amount} €, ab {fmt(newPlan.first_due_date)}, jeweils am {newPlan.day_of_month}. des Monats
                    </p>
                  )}
                  <div className="flex gap-2">
                    <button onClick={createPlan} disabled={saving} className="px-4 py-2 bg-[#970000] text-white rounded-full text-sm font-medium hover:bg-[#7a0000] disabled:opacity-50">
                      {saving ? 'Wird angelegt…' : 'Anlegen & Raten generieren'}
                    </button>
                    <button onClick={() => setShowNewPlan(false)} className="px-4 py-2 border border-[#d9c9bd] rounded-full text-sm text-[#6b6b6b] hover:bg-[#faf7f5]">Abbrechen</button>
                  </div>
                </div>
              )}

              {/* Plan settings */}
              {selected.plan_id && (
                <div className="border border-[#eadfd6] rounded-2xl p-4 space-y-4">
                  <h4 className="font-semibold text-[#111827] text-sm">Zahlungsplan-Einstellungen</h4>
                  {selected.stripe_subscription_id
                    && (selected.plan_status === 'active' || selected.plan_status === 'paused')
                    && payments.length > 0
                    && payments.every(p => p.status === 'paid' || p.status === 'cancelled') && (
                    <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5 text-xs text-amber-800">
                      <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                      <p>
                        Alle Raten sind bezahlt, das Stripe-Abo läuft aber weiter und bucht
                        weiterhin monatlich ab. Füge weitere Raten hinzu, falls der Kurs noch
                        läuft — oder setze den Plan auf <strong>„Abgeschlossen"</strong>, um das
                        Abo zum Periodenende zu beenden.
                      </p>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={labelCls}>Plan-Status</label>
                      <select value={editPlanStatus} onChange={e => setEditPlanStatus(e.target.value)} className={inputCls}>
                        <option value="active">Aktiv</option>
                        <option value="paused">Pausiert</option>
                        <option value="completed">Abgeschlossen</option>
                        <option value="cancelled">Storniert</option>
                      </select>
                    </div>
                    <div>
                      <label className={labelCls}>Zugriff bis</label>
                      <input type="date" value={editAccessUntil} onChange={e => setEditAccessUntil(e.target.value)} className={inputCls} />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Manuelle Freischaltung</label>
                    <button
                      onClick={() => setEditOverride(v => !v)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-2xl border text-sm font-medium transition-colors ${editOverride ? 'bg-green-50 border-green-200 text-green-700' : 'border-[#ddd6cf] text-[#6b6b6b]'}`}
                    >
                      {editOverride ? <ToggleRight className="h-5 w-5" /> : <ToggleLeft className="h-5 w-5" />}
                      {editOverride ? 'Freigeschalten' : 'Nicht freigeschalten'}
                    </button>
                    {editOverride && (
                      <input value={editOverrideReason} onChange={e => setEditOverrideReason(e.target.value)} placeholder="Grund (z.B. Kulanz)" className={`${inputCls} mt-2`} />
                    )}
                  </div>
                  <div>
                    <label className={labelCls}>Notizen</label>
                    <textarea value={editNotes} onChange={e => setEditNotes(e.target.value)} className={`${inputCls} resize-none`} rows={2} />
                  </div>
                  <div className="flex items-center justify-between">
                    <button onClick={saveSettings} disabled={saving} className="px-4 py-2 bg-[#970000] text-white rounded-full text-sm font-medium hover:bg-[#7a0000] disabled:opacity-50">
                      {saving ? 'Speichern…' : 'Einstellungen speichern'}
                    </button>
                    <button onClick={deletePlan} className="inline-flex items-center gap-1.5 px-3 py-2 border border-red-200 text-red-600 rounded-full text-xs font-medium hover:bg-red-50 transition-colors">
                      <Trash2 className="h-3.5 w-3.5" /> Plan löschen
                    </button>
                  </div>
                </div>
              )}

              {/* Payments */}
              {selected.plan_id && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-[#111827] text-sm">Zahlungen</h4>
                    <button onClick={() => setShowNewPayment(v => !v)} className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#d9c9bd] rounded-full text-xs font-medium text-[#6b6b6b] hover:bg-[#faf7f5]">
                      <Plus className="h-3.5 w-3.5" /> Einzeln hinzufügen
                    </button>
                  </div>

                  {/* Single payment form */}
                  {showNewPayment && (
                    <div className="border border-[#eadfd6] rounded-2xl p-4 space-y-3 bg-[#faf7f5]">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className={labelCls}>Betrag (€)</label>
                          <input type="number" value={newPayment.amount} onChange={e => setNewPayment(p => ({ ...p, amount: e.target.value }))} className={inputCls} placeholder="0.00" />
                        </div>
                        <div>
                          <label className={labelCls}>Fälligkeit</label>
                          <input type="date" value={newPayment.due_date} onChange={e => setNewPayment(p => ({ ...p, due_date: e.target.value }))} className={inputCls} />
                        </div>
                        <div>
                          <label className={labelCls}>Status</label>
                          <select value={newPayment.status} onChange={e => setNewPayment(p => ({ ...p, status: e.target.value }))} className={inputCls}>
                            <option value="open">Offen</option>
                            <option value="paid">Bezahlt</option>
                            <option value="overdue">Überfällig</option>
                            <option value="cancelled">Storniert</option>
                          </select>
                        </div>
                        <div>
                          <label className={labelCls}>Zahlungsart</label>
                          <select value={newPayment.payment_method} onChange={e => setNewPayment(p => ({ ...p, payment_method: e.target.value }))} className={inputCls}>
                            <option value="">–</option>
                            <option value="bank_transfer">Überweisung</option>
                            <option value="cash">Bar</option>
                            <option value="stripe">Stripe</option>
                            <option value="other">Sonstiges</option>
                          </select>
                        </div>
                        <div className="col-span-2">
                          <label className={labelCls}>Rechnungslink (optional)</label>
                          <input type="url" value={newPayment.invoice_url} onChange={e => setNewPayment(p => ({ ...p, invoice_url: e.target.value }))} className={inputCls} placeholder="https://…" />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={addPayment} disabled={saving || !newPayment.due_date} className="px-4 py-2 bg-[#970000] text-white rounded-full text-sm font-medium hover:bg-[#7a0000] disabled:opacity-50">
                          {saving ? 'Hinzufügen…' : 'Hinzufügen'}
                        </button>
                        <button onClick={() => setShowNewPayment(false)} className="px-4 py-2 border border-[#d9c9bd] rounded-full text-sm text-[#6b6b6b] hover:bg-[#faf7f5]">Abbrechen</button>
                      </div>
                    </div>
                  )}

                  {paymentsLoading ? (
                    <div className="flex justify-center py-4"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#970000]" /></div>
                  ) : payments.length === 0 ? (
                    <p className="text-sm text-[#6b6b6b] text-center py-4">Noch keine Zahlungen</p>
                  ) : (
                    <div className="overflow-hidden border border-[#eadfd6] rounded-2xl">
                      <table className="w-full text-sm">
                        <thead className="bg-[#faf7f5] border-b border-[#eadfd6]">
                          <tr>
                            {['Fälligkeit', 'Betrag', 'Status', 'Aktionen', 'Zahlungsart', 'Rechnung'].map(h => (
                              <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-[#6b6b6b]">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-[#f3ede8]">
                          {payments.map(pay => (
                            <tr
                              key={pay.id}
                              className={`transition-colors duration-700 ${flashId === pay.id ? 'bg-green-50' : 'hover:bg-[#faf7f5]'}`}
                            >
                              <td className="px-3 py-2 text-[#6b6b6b] whitespace-nowrap">{fmt(pay.due_date)}</td>
                              <td className="px-3 py-2 font-medium text-[#111827]">{fmtAmount(pay.amount)}</td>
                              <td className="px-3 py-2">
                                <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLOR[pay.status] ?? 'bg-gray-100 text-gray-600'}`}>
                                  {STATUS_LABEL[pay.status] ?? pay.status}
                                </span>
                              </td>
                              {/* Quick actions */}
                              <td className="px-3 py-2">
                                <div className="flex items-center gap-1">
                                  {pay.status !== 'paid' && (
                                    <button
                                      onClick={() => quickPay(pay)}
                                      title="Als bezahlt markieren"
                                      className="p-1.5 rounded-lg bg-green-50 text-green-700 hover:bg-green-100 transition-colors"
                                    >
                                      <Check className="h-3.5 w-3.5" />
                                    </button>
                                  )}
                                  {pay.status === 'open' && (
                                    <button
                                      onClick={() => quickOverdue(pay)}
                                      title="Als überfällig markieren"
                                      className="p-1.5 rounded-lg bg-red-50 text-red-700 hover:bg-red-100 transition-colors"
                                    >
                                      <AlertCircle className="h-3.5 w-3.5" />
                                    </button>
                                  )}
                                  {pay.status === 'paid' && (
                                    <button
                                      onClick={() => { updatePayment(pay.id, { status: 'open', paid_date: null }); flash(pay.id); }}
                                      title="Zahlung rückgängig machen"
                                      className="p-1.5 rounded-lg bg-gray-50 text-gray-500 hover:bg-gray-100 transition-colors"
                                    >
                                      <RotateCcw className="h-3.5 w-3.5" />
                                    </button>
                                  )}
                                  <button
                                    onClick={() => deletePayment(pay.id)}
                                    title="Zahlung löschen"
                                    className="p-1.5 rounded-lg bg-red-50 text-red-400 hover:bg-red-100 hover:text-red-700 transition-colors"
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                              </td>
                              <td className="px-3 py-2">
                                <select
                                  value={pay.payment_method ?? ''}
                                  onChange={e => updatePayment(pay.id, { payment_method: e.target.value || null })}
                                  className="text-xs border border-[#ddd6cf] rounded-xl px-2 py-1 text-[#6b6b6b]"
                                >
                                  <option value="">–</option>
                                  <option value="bank_transfer">Überweisung</option>
                                  <option value="cash">Bar</option>
                                  <option value="stripe">Stripe</option>
                                  <option value="other">Sonstiges</option>
                                </select>
                              </td>
                              {/* Rechnung: generiertes PDF oder Legacy-Link */}
                              <td className="px-3 py-2">
                                {(() => {
                                  const issuedInv = invoices.find(i => i.payment_id === pay.id && i.status === 'issued');
                                  const cancelledInv = issuedInv ? null : invoices.find(i => i.payment_id === pay.id && i.status === 'cancelled');
                                  if (issuedInv) {
                                    return (
                                      <div className="flex items-center gap-1">
                                        <span className="text-xs text-[#6b6b6b] whitespace-nowrap">{issuedInv.invoice_number}</span>
                                        {issuedInv.pdf_path && (
                                          <button onClick={() => downloadInvoice(issuedInv)} title="PDF herunterladen" className="p-1 rounded-lg text-[#970000] hover:bg-[#faf7f5] transition-colors">
                                            <Download className="h-3.5 w-3.5" />
                                          </button>
                                        )}
                                        <button onClick={() => openInvoiceEdit(issuedInv)} title="Rechnung bearbeiten" className="p-1 rounded-lg text-[#6b6b6b] hover:bg-[#faf7f5] hover:text-[#111827] transition-colors">
                                          <Pencil className="h-3.5 w-3.5" />
                                        </button>
                                      </div>
                                    );
                                  }
                                  if (pay.status === 'paid') {
                                    return (
                                      <div className="flex items-center gap-1.5">
                                        {cancelledInv && (
                                          <span className="text-[10px] text-gray-400 whitespace-nowrap" title={`${cancelledInv.invoice_number} storniert`}>storniert</span>
                                        )}
                                        <button
                                          onClick={() => createInvoice(pay.id)}
                                          disabled={invoiceBusy === pay.id}
                                          className="inline-flex items-center gap-1 text-xs font-medium text-[#970000] hover:underline disabled:opacity-50"
                                        >
                                          {invoiceBusy === pay.id
                                            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                            : <FileText className="h-3.5 w-3.5" />}
                                          Erstellen
                                        </button>
                                        <button
                                          onClick={() => { setUploadForPayment(pay); setUploadNumber(''); setUploadFile(null); }}
                                          title="Extern erstellte Rechnung (PDF) hochladen, z. B. aus sevdesk"
                                          className="p-1 rounded-lg text-[#6b6b6b] hover:bg-[#faf7f5] hover:text-[#111827] transition-colors"
                                        >
                                          <Upload className="h-3.5 w-3.5" />
                                        </button>
                                      </div>
                                    );
                                  }
                                  return editingInvoice === pay.id ? (
                                    <div className="flex items-center gap-1">
                                      <input
                                        autoFocus
                                        value={invoiceInput}
                                        onChange={e => setInvoiceInput(e.target.value)}
                                        onKeyDown={e => {
                                          if (e.key === 'Enter') saveInvoice(pay.id);
                                          if (e.key === 'Escape') cancelInvoiceEdit();
                                        }}
                                        placeholder="https://…"
                                        className="text-xs border border-[#ddd6cf] rounded-xl px-2 py-1 w-36"
                                      />
                                      <button onClick={() => saveInvoice(pay.id)} className="p-1 text-green-700 hover:text-green-900" title="Speichern">
                                        <Check className="h-3.5 w-3.5" />
                                      </button>
                                      <button onClick={cancelInvoiceEdit} className="p-1 text-[#6b6b6b] hover:text-[#111827]" title="Abbrechen (Esc)">
                                        <X className="h-3.5 w-3.5" />
                                      </button>
                                    </div>
                                  ) : (
                                    <button
                                      onClick={() => { setEditingInvoice(pay.id); setInvoiceInput(pay.invoice_url ?? ''); }}
                                      className={`inline-flex items-center gap-1 text-xs font-medium transition-colors ${pay.invoice_url ? 'text-[#970000] hover:underline' : 'text-[#6b6b6b] hover:text-[#970000]'}`}
                                    >
                                      <Link className="h-3.5 w-3.5" />
                                      {pay.invoice_url ? 'Öffnen' : 'Link setzen'}
                                    </button>
                                  );
                                })()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentsManager;
